// Libs
import classNames from 'classnames/bind';
import { Box, Button, FormHelperText, Grid2, InputLabel, Rating, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { ReviewComplete } from '~/components/module/review';
import { BaseSelect, BaseTextEditor, Loading } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_RATING,
  EMPTY_STRING,
  QUERY_TOKEN,
} from '~/utils/constants/common';
import { ModeTextEditorEnum } from '~/utils/enum/common';
import { IEmployee, IQueryEmployeeReview } from '~/utils/interface/employee';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getEmployeeReview } from '~/thunks/employee/employeeThunk';
import useDebounce from '~/utils/hooks/useDebounce';
import { getFullName } from '~/utils/helper/common';
import { createReview } from '~/thunks/review/reviewThunk';
import { IFormReview } from '~/utils/interface/review';
import { defaultValuesForm, schema } from './helper';
// Styles, images, icons
import styles from './FormJobReview.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormJobReview = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/review']);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const token = searchParams.get(QUERY_TOKEN);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormReview>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<IEmployee[]>([]);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetEmployeeList = useCallback(
    (params: IQueryEmployeeReview) => {
      setIsLoadingEmployee(true);
      dispatch(getEmployeeReview(params))
        .unwrap()
        .then((res) => {
          const { responses } = res?.data;
          if (!responses) return;

          setEmployeeList(responses);
        })
        .catch((err) => {})
        .finally(() => setIsLoadingEmployee(false));
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!token) return;
    const params: IQueryEmployeeReview = {
      token: token,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetEmployeeList(params);
  }, [token, debounceEmployeeSearchKey, handleGetEmployeeList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchEmployeeChange = (searchKey: string) => {
    setEmployeeSearchKey(searchKey);
  };

  const handleSubmitFormReview = (data: IFormReview) => {
    if (token) {
      setIsLoading(true);
      const { content, ...restData } = data;
      const body: IFormReview = {
        ...restData,
        ...(content ? { content: content } : {}),
        token: token,
      };
      dispatch(createReview(body))
        .unwrap()
        .then((res) => {
          setIsShowAlert(true);
        })
        .catch((err) => {})
        .finally(() => setIsLoading(false));
    } else {
      setIsShowAlert(true);
    }
  };

  const labelsRating: { [index: string]: string } = {
    1: t('form_review_bad_label'),
    2: t('form_review_not_satisfied_label'),
    3: t('form_review_normal_label'),
    4: t('form_review_satisfied_label'),
    5: t('form_review_great_label'),
  };

  const handleCloseAlert = () => {
    setIsShowAlert(false);
  };
  //#endregion Handle Function

  return (
    <Box id='formJobReview' component={'div'} className={cx('container')}>
      <Box component={'form'} className={cx('form')} onSubmit={handleSubmit(handleSubmitFormReview)}>
        <Box component={'div'} className={cx('formBody')}>
          <Grid2 container size={12} rowSpacing={'20px'} columnSpacing={'12px'}>
            <Grid2 size={12}>
              <Controller
                control={control}
                name='employees'
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseSelect
                      multiple={true}
                      value={value}
                      onChange={onChange}
                      label={t('form_review_employee_label')}
                      options={
                        employeeList?.map((employee) => ({
                          label: getFullName({ ...employee }),
                          value: employee.id,
                        })) || []
                      }
                      isLoading={isLoadingEmployee}
                      onSearchChange={handleSearchEmployeeChange}
                      errorMessage={errors?.employees?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                control={control}
                name='rating'
                render={({ field: { value, onChange } }) => {
                  return (
                    <Box>
                      <InputLabel required={true} className={cx('label')}>
                        {t('form_review_rating_label')}
                      </InputLabel>
                      <Box component={'div'} className={cx('ratingWrap')}>
                        <Rating
                          value={value}
                          defaultValue={DEFAULT_RATING}
                          size='large'
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                        />

                        <Box sx={{ ml: 1 }} className={cx('descRating')}>
                          {labelsRating[value]}
                        </Box>
                      </Box>

                      {errors?.rating?.message && (
                        <FormHelperText className={cx('errorMessage')}>
                          {errors.rating?.message}
                        </FormHelperText>
                      )}
                    </Box>
                  );
                }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                control={control}
                name='content'
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseTextEditor
                      mode={ModeTextEditorEnum.CREATE}
                      height={200}
                      label={t('form_review_content_label')}
                      value={value}
                      onChange={(textEditor: string) => onChange(textEditor)}
                      errorMessage={errors.content?.message}
                    />
                  );
                }}
              />
            </Grid2>
          </Grid2>
        </Box>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='contained' type='submit'>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>

        {isLoading && <Loading />}
      </Box>

      <ReviewComplete isOpen={isShowAlert} onClose={handleCloseAlert} />
    </Box>
  );
};

export default FormJobReview;
